import React, {FC, FormEvent, ChangeEvent, useState, useEffect, useRef} from 'react';
import Column from "../Column";
import Row from "../Row";
import PlusButton from "../PlusButton";
import XButton from "../XButton";
import styled from "styled-components/macro";
import TextArea from "../TextArea";
import Switch from "../Switch";
import noop from "../../utils/noop";
import {ChoiceBranchingRuleComponent, ChoiceBranchingRuleDefaultValue, PerChoiceBranchingRuleComponent, PerChoiceBranchingRuleDefaultValue} from "./ChoiceBranchingRule";
import {ChoiceQuestion, ChoiceBranchingRule, ChoiceRiskRule} from '../../models'
import Required from "../Required";
import {checkIfAllValuesUndefined, StringValidator} from "../../shared/utils/Validators";
import {ValidatedInput} from "../Validation";
import {branchingRuleReservedValues} from "./BranchingRule";
import { ChoiceRiskRuleComponent, ChoiceRiskRuleDefaultValue } from "./ChoiceRiskRule"
import { removedChoiceIDBranchingRule, removedChoiceIDRiskRule, updatedChoiceBranchingRule } from '../../utils/choiceQuestionUtils';

interface MultipleChoiceQuestionProps {
  value?: ChoiceQuestion;
  surveyQuestionIDs: string[];
  currentQuestionIndex: number;
  onChange?: (value: ChoiceQuestion) => any;
  onSubmit?: (event: FormEvent<HTMLFormElement>) => any;
  canSwitch?: (value: boolean) => any;
  showRiskToggle?: boolean;
}

export const ChoiceQuestionDefaultValue: ChoiceQuestion = {
  questionId: "",
  questionHeader: "",
  questionBody: "",
  skipEnabled: true,
  multipleChoice: false,
  textChoices: [],
  branchingRule: undefined
};

/**
 * Converts a legacy choice branching rule to the per-choice format used by
 * single-select questions.
 *
 * Legacy rules have one destination shared by all selected choices. The
 * per-choice format stores that same destination once for each selected choice
 * and keeps the existing fallback destination unchanged.
 */
export function toPerChoiceBranchingRule(
  legacyRule: ChoiceBranchingRule
): ChoiceBranchingRule {
  const selectedChoiceIDs = (legacyRule.expectedAnswerValues ?? []).filter(
    (choiceID): choiceID is string => choiceID !== null
  );
  const uniqueSelectedChoiceIDs = selectedChoiceIDs.filter(
    (choiceID, index, choiceIDs) => choiceIDs.indexOf(choiceID) === index
  );

  return {
    ...PerChoiceBranchingRuleDefaultValue,
    defaultQuestionId: legacyRule.defaultQuestionId ?? "",
    destinations: uniqueSelectedChoiceIDs.map(choiceId => ({
      choiceId,
      destinationQuestionId: legacyRule.destinationQuestionId ?? ""
    }))
  };
}

const ChoiceQuestionComponent: FC<MultipleChoiceQuestionProps> = props => {
    const {
    value = ChoiceQuestionDefaultValue,
    onChange = noop,
    canSwitch = noop,
    showRiskToggle,
  } = props;
  const [validation, setValidation] = useState<Record<string, string | undefined>>({});
  const [choiceValidation, setChoiceValidation] = useState<Array<Record<string, string | undefined>>>(
    value.textChoices?.map((_) => {
      return {'textChoiceID': undefined, 'textToDisplay': undefined};
    }) ?? []);
  const pendingChoiceIDRenames = useRef<Record<number, string>>({});

  const nextQuestion = props.surveyQuestionIDs.length > props.currentQuestionIndex+1 ? props.surveyQuestionIDs[props.currentQuestionIndex+1] : undefined

  useEffect(() => {
    setChoiceValidation(value.textChoices?.map((_) => {
      return {'textChoiceID': undefined, 'textToDisplay': undefined};
    }) ?? [])
  }, [value.questionId])
  

  useEffect(() => {
    let validationIsGood = checkIfAllValuesUndefined(validation);
    // check the high level values
    let validationChecks =
    [ new StringValidator(value.questionId).min(1).validate(),
      new StringValidator(value.questionHeader ?? '').min(1).validate()
    ];
    if (value.branchingRule) {
      // The presence of `destinations` identifies the per-choice format. Only
      // checked choices have individual destinations; all others use the fallback.
      if (value.branchingRule.destinations !== undefined && value.branchingRule.destinations !== null) {
        validationChecks.push(
          value.branchingRule.defaultQuestionId ? undefined : "Value cannot be empty",
          ...value.branchingRule.destinations.map(destination =>
            destination?.destinationQuestionId ? undefined : "Value cannot be empty"
          )
        );
      } else {
        // Legacy branching uses one destination shared by all selected choices.
        validationChecks.push(
          value.branchingRule.defaultQuestionId ? undefined : "Value cannot be empty",
          value.branchingRule.destinationQuestionId ? undefined : "Value cannot be empty"
        );
      }
    }
    validationChecks.forEach((value) => {
      validationIsGood = validationIsGood && value === undefined ;
    });
    // check the values inside each text choice
    value.textChoices?.forEach((choice) => {
      if (choice) {
        validationIsGood = validationIsGood &&
          new StringValidator(choice.textChoiceID).min(1).validate() === undefined;
        validationIsGood = validationIsGood &&
          new StringValidator(choice.textToDisplay).min(1).validate() === undefined;
      }
    });
    // check for errors in the text choice validations
    choiceValidation.forEach((choiceVali) => {
      validationIsGood = validationIsGood && checkIfAllValuesUndefined(choiceVali);
    });
    canSwitch(validationIsGood);
  },)


  function handleQuestionIdChange(event: FormEvent<HTMLInputElement>) {
    const error = new StringValidator(event.currentTarget.value, 'QuestionID must contain at least one character')
      .alphaNum('Question ID can only contain of numbers of letters.')
      .min(1, 'Question ID must contain at least one character.')
      .unique(props.surveyQuestionIDs, 'Question IDs must be unique (match found for another question)', props.currentQuestionIndex)
      .unique(branchingRuleReservedValues, 'This value is reserved and cannot be used as a Question ID')
      .validate();
    setValidation({
      ...validation,
      questionId: error
    });
    onChange({
      ...value,
      questionId: event.currentTarget.value
    });
  }

  function handleQuestionHeaderChange(event: FormEvent<HTMLInputElement>) {
    const error = new StringValidator(event.currentTarget.value)
      .min(1, 'Question Header must contain at least one character')
      .noSpace('Question Header value cannot contain only spaces')
      .validate();
    setValidation({
      ...validation,
      questionHeader: error
    });
    onChange({
      ...value,
      questionHeader: event.currentTarget.value
    });
  }

  function handleQuestionBodyChange(event: FormEvent<HTMLTextAreaElement>) {
    onChange({
      ...value,
      questionBody: event.currentTarget.value
    });
  }

  function addTextChoices(_: FormEvent<HTMLButtonElement>) {
    setChoiceValidation([...choiceValidation, {textChoiceID: undefined, textToDisplay: undefined}]);
    onChange({
      ...value,
      textChoices: [...value.textChoices!, {textChoiceID: "", textToDisplay:""}]
    });
  }

  function toggleSelect(newState: boolean) {
    const perChoiceDestinations = value.branchingRule?.destinations;
    // Per-choice destinations are only valid for single-select questions. An
    // empty list has no per-choice routes, so it can safely return to the
    // legacy Select All format.
    if (perChoiceDestinations !== undefined && perChoiceDestinations !== null && perChoiceDestinations.length > 0) {
      return;
    }

    // When switching an existing legacy rule to single-select, preserve its
    // routes by converting the shared destination into one route per choice.
    let branchingRule = value.branchingRule;
    if (!newState && value.branchingRule) {
      branchingRule = toPerChoiceBranchingRule(value.branchingRule);
    } else if (newState && perChoiceDestinations?.length === 0 && value.branchingRule) {
      branchingRule = {
        ...ChoiceBranchingRuleDefaultValue,
        defaultQuestionId: value.branchingRule.defaultQuestionId
      };
    }

    onChange({
      ...value,
      multipleChoice: newState,
      branchingRule
    });
  }

  function toggleLogic(newState: boolean) {
    if (nextQuestion && newState) {
      onChange({
        ...value,
        branchingRule: value.multipleChoice
          ? ChoiceBranchingRuleDefaultValue
          : PerChoiceBranchingRuleDefaultValue
      });
    } else {
      onChange({
        ...value,
        branchingRule: undefined
      });
    }
  }

  function toggleRisk (newState: boolean) {
    onChange({
      ...value,
      riskRule: newState ? {
        ...ChoiceRiskRuleDefaultValue
      } : undefined
    })
  }

  function toggleSkip(newState: boolean) {
    onChange({
      ...value,
      skipEnabled: newState
    });
  }

  const updateTextChoice = (key: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const error = new StringValidator(event.currentTarget.value, 'Choice Text must contain at least one character')
      .min(1, 'Choice Text must contain at least one character.')
      .validate();
    const choiceValidations = [...choiceValidation];
    choiceValidations[key].textToDisplay= error;
    setChoiceValidation(choiceValidations);
    const choices = [...value.textChoices!]
    if (choices[key]) {
      choices[key] = {...choices[key]!, textToDisplay: event.currentTarget.value};
    }
    onChange({
      ...value,
      textChoices: choices
    });
  }

  const updateTextChoiceId = (key: number) => (event: ChangeEvent<HTMLInputElement>) => {
    const choiceIDs = value.textChoices?.filter(item => item !== null && item.textChoiceID !== undefined).map(item => item!.textChoiceID) as string[];
    const error = new StringValidator(event.currentTarget.value, 'Choice ID must contain at least one character')
      .alphaNum('Choice ID can only contain of numbers of letters.')
      .min(1, 'Choice ID must contain at least one character.')
      .unique(choiceIDs, 'Choice IDs must be unique to each choice', key)
      .validate();
    const choiceValidations = [...choiceValidation];
    choiceValidations[key].textChoiceID = error;
    setChoiceValidation(choiceValidations);
    const choices = [...value.textChoices!];
    let newBranchingRule = value.branchingRule;

    if (choices[key]) {
      // Only rename branching references after a valid choice ID is entered.
      // Invalid intermediate values must still be reflected in this controlled input.
      if (error === undefined) {
        const oldChoiceID = pendingChoiceIDRenames.current[key] ??
          choices[key]!.textChoiceID;
        newBranchingRule = updatedChoiceBranchingRule(
          oldChoiceID,
          event.currentTarget.value,
          value.branchingRule
        );
        delete pendingChoiceIDRenames.current[key];
      } else if (pendingChoiceIDRenames.current[key] === undefined) {
        pendingChoiceIDRenames.current[key] = choices[key]!.textChoiceID;
      }
      choices[key] = {...choices[key]!, textChoiceID: event.currentTarget.value};
    }
    onChange({
      ...value,
      textChoices: choices,
      branchingRule: newBranchingRule
    });
  }

  const removeTextChoice = (key: number) => (event: ChangeEvent<HTMLButtonElement>) => {
    const choices = [...value.textChoices!];

    //update branching rule
    const choiceID = choices[key].textChoiceID;
    let newBranchingRule = removedChoiceIDBranchingRule(choiceID, value.branchingRule)

    //update risk rule
    let newRiskRule =  removedChoiceIDRiskRule(choiceID, value.riskRule)


    choices.splice(key, 1);
    const pendingChoiceIDRenamesAfterRemoval: Record<number, string> = {};
    Object.entries(pendingChoiceIDRenames.current).forEach(([index, choiceID]) => {
      const pendingIndex = Number(index);
      if (pendingIndex < key) {
        pendingChoiceIDRenamesAfterRemoval[pendingIndex] = choiceID;
      } else if (pendingIndex > key) {
        pendingChoiceIDRenamesAfterRemoval[pendingIndex - 1] = choiceID;
      }
    });
    pendingChoiceIDRenames.current = pendingChoiceIDRenamesAfterRemoval;
    const choiceValidations = [...choiceValidation];
    choiceValidations.splice(key, 1);
    setChoiceValidation(choiceValidations);
    onChange({
      ...value,
      textChoices: choices,
      branchingRule: newBranchingRule,
      riskRule: newRiskRule
    });
  }


  function updateBranchingRule(rule: ChoiceBranchingRule) {
    onChange({
      ...value,
      branchingRule: rule
    })
  }

  function updateRiskRule(rule: ChoiceRiskRule) {
    onChange({
      ...value,
      riskRule: rule
    })
  }

  return (
    <Column>
      <h2>Choice Question</h2>
      <Required>
        <label>Question ID</label>
      </Required>
      <ValidatedInput
        message={validation.questionId}
        value={value.questionId}
        onChange={handleQuestionIdChange}
        placeholder="Question ID"
      />
      <Required>
        <label>Question Header</label>
      </Required>
      <ValidatedInput
        message={validation.questionHeader}
        value={value.questionHeader!}
        onChange={handleQuestionHeaderChange}
        placeholder="Question Header"
      />
      <label>Question Body</label>
      <QuestionTextInput
        value={value.questionBody}
        onChange={handleQuestionBodyChange}
        placeholder="Question Body"
      />
      <Row>
        <ol>
        { value.textChoices && value.textChoices.map((choice, index) => {
          return <TextChoice key={index+value.questionId}>
            <MultipleChoiceRow>
              <ValidatedInput
                message={choiceValidation[index]?.textChoiceID}
                onChange={updateTextChoiceId(index)}
                value={choice?.textChoiceID}
                placeholder={'Choice ID'}
              />
              <ValidatedInput
                message={choiceValidation[index]?.textToDisplay}
                onChange={updateTextChoice(index)}
                value={choice?.textToDisplay}
                placeholder={'Choice Text'}
              />
              <XButton
                onClick={removeTextChoice(index)}
              />
              <Required><span/></Required>
            </MultipleChoiceRow>
          </TextChoice>
          })
        }
        </ol>
      </Row>
      <PlusButton
        onClick={addTextChoices}
      />
      {showRiskToggle && 
      <Row>
        <Switch
          checked={value.riskRule !== undefined && value.riskRule !== null}
          onChange={toggleRisk}
        />
        <label>Risk Alert</label>
      </Row>
      }
      {showRiskToggle && value.riskRule && 
        <Row>
          <ChoiceRiskRuleComponent
            value={value.riskRule}
            choiceQuestionIDs={value.textChoices.map((item) => item?.textChoiceID!)}
            onChange={updateRiskRule}
          />
        </Row>
      }
      <Row>
        <Switch
          checked={value.skipEnabled}
          onChange={toggleSkip}
        />
        <Label>Skippable</Label>
      </Row>
      <Row>
        <Switch
          checked={value.multipleChoice}
          onChange={toggleSelect}
          disabled={(value.branchingRule?.destinations?.length ?? 0) > 0}
        />
        <Label>Select All</Label>
        {(value.branchingRule?.destinations?.length ?? 0) > 0 &&
          <span>. Per-choice branching is only supported for single-select questions.</span>
        }
      </Row>
      {nextQuestion && value.textChoices && value.textChoices.length > 0 &&
        <Row>
          <Switch
            checked={value.branchingRule !== undefined && value.branchingRule !== null}
            onChange={toggleLogic}
          />
          <Label>Logic</Label>
        </Row>
      }
      {value.branchingRule &&
        <div>
          {value.textChoices && value.textChoices.length > 0 && (
            value.branchingRule.destinations !== undefined && value.branchingRule.destinations !== null && !value.multipleChoice
              ? <PerChoiceBranchingRuleComponent
                  surveyQuestionIDs={props.surveyQuestionIDs.slice(props.currentQuestionIndex+1)}
                  choiceQuestionIDs={value.textChoices.map((item) => item?.textChoiceID!)}
                  value={value.branchingRule}
                  onChange={updateBranchingRule}
                />
              : <ChoiceBranchingRuleComponent
                  surveyQuestionIDs={props.surveyQuestionIDs.slice(props.currentQuestionIndex+1)}
                  choiceQuestionIDs={value.textChoices.map((item) => item?.textChoiceID!)}
                  value={value.branchingRule}
                  onChange={updateBranchingRule}
                />
            )}
        </div>
      }
    </Column>
  );

}


export default styled(ChoiceQuestionComponent)`
  min-width: 100%;
`;


export const QuestionTextInput = styled(TextArea as any)`
  width: 100%;
  margin-bottom: 16px;
`;

const Label = styled('label')`
    margin-left: .5em;
`;

const TextChoice = styled('li')`
    margin-bottom: .5em;
`;

const MultipleChoiceRow = styled(Row as any)`
  > * {
    margin: 2px;
  }
`;

